// Lib
import { isEmpty, last } from 'lodash/fp';

// Utils
import { getIsTopLevelSharedBoard } from '../../../../../../common/permissions/elementPermissionsUtil';
import { getElementId, getTitle } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { isSkeleton } from '../../../../../../common/elements/utils/elementTypeUtils';
import { getClosestAncestorBoardId, getElement } from '../../../../../../common/elements/utils/elementTraversalUtils';
import {
    getNotificationAncestorTitlesAsArray,
    getNotificationBoardTitle,
    getNotificationElementId,
} from '../../../../../../common/notifications/notificationModelUtils';

// Constants
import { NOTIFICATION_GROUP_TYPES } from '../popup/entry/utils/notificationEntryConstants';

const getGroupFromElement = ({ elements, elementId }) => {
    const groupElement = getElement(elements, elementId);

    return {
        groupType: NOTIFICATION_GROUP_TYPES.ELEMENT,
        id: getElementId(groupElement),
        title: getTitle(groupElement),
    };
};

const getGroupFromAncestorTitles = ({ ancestorTitles }) => ({
    groupType: NOTIFICATION_GROUP_TYPES.ELEMENT,
    title: last(ancestorTitles),
});

const getGroupFromElementId = ({ elementId, notification }) => ({
    groupType: NOTIFICATION_GROUP_TYPES.ELEMENT,
    id: elementId,
    title: getNotificationBoardTitle(notification),
});

const getGeneralGroup = () => ({
    groupType: NOTIFICATION_GROUP_TYPES.GENERAL,
    id: NOTIFICATION_GROUP_TYPES.GENERAL,
    title: NOTIFICATION_GROUP_TYPES.GENERAL,
});

export default ({ elements, notification, userId }) => {
    const elementId = getNotificationElementId(notification);
    const element = getElement(elements, elementId);

    if (element && !isSkeleton(element)) return getGroupFromElement({ elements, elementId, userId, notification });

    // Fallback to the stored ancestor titles if the element couldn't be used
    const ancestorTitles = getNotificationAncestorTitlesAsArray(notification);
    if (!isEmpty(ancestorTitles)) return getGroupFromAncestorTitles({ ancestorTitles, notification });

    // If we have an element ID but we don't have the element, then it must have been deleted
    if (elementId) return getGroupFromElementId({ elementId, notification });

    return getGeneralGroup();
};

const getParentGroupFromElement = ({ elements, elementId, userId }) => {
    const isTopLevelSharedBoard = getIsTopLevelSharedBoard(elements, elementId, userId);

    if (isTopLevelSharedBoard) return null;

    const groupElementId = getClosestAncestorBoardId(elements, elementId);

    const groupElement = getElement(elements, groupElementId);

    return {
        groupType: NOTIFICATION_GROUP_TYPES.ELEMENT,
        id: getElementId(groupElement) || groupElementId,
        title: getTitle(groupElement),
    };
};

const getParentGroupFromAncestorTitles = ({ ancestorTitles }) => {
    if (!ancestorTitles || ancestorTitles.length <= 1) return null;

    return {
        groupType: NOTIFICATION_GROUP_TYPES.ELEMENT,
        title: ancestorTitles[ancestorTitles.length - 2],
    };
};

export const getNotificationParentGroup = ({ elements, notification, userId }) => {
    const elementId = getNotificationElementId(notification);
    const element = getElement(elements, elementId);

    if (element && !isSkeleton(element)) {
        return getParentGroupFromElement({ elements, elementId, userId, notification });
    }

    // Fallback to the stored ancestor titles if the element couldn't be used
    const ancestorTitles = getNotificationAncestorTitlesAsArray(notification);
    if (!isEmpty(ancestorTitles)) return getParentGroupFromAncestorTitles({ ancestorTitles, notification });

    // If we have an element ID but we don't have the element, then it must have been deleted
    if (elementId) return null;

    return null;
};
